/**
 * 商品详情页 - 中式典雅风格
 * 配色：宣纸白 (#F8F6F1)、茶褐 (#7D6B55)、竹青 (#6B8E7C)、朱砂红 (#C44536)
 */
import React, { useEffect, useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Row,
  Col,
  Typography,
  Button,
  InputNumber,
  Spin,
  Tag,
  Space,
  Divider,
  Image,
  message,
  Breadcrumb,
  Grid,
} from 'antd';
import {
  ShoppingCartOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  HomeOutlined,
  ThunderboltOutlined,
} from '@ant-design/icons';
import StorefrontLayout from '../../components/storefront/StorefrontLayout';
import { productApi } from '../../api/index';
import { useCart } from '../../contexts/CartContext';

const { Title, Text, Paragraph } = Typography;

// 中式配色
const COLORS = {
  paper: '#F8F6F1',
  teaBrown: '#7D6B55',
  bambooGreen: '#6B8E7C',
  cinnabar: '#C44536',
  ink: '#2C2C2C',
  lightInk: '#5A5A5A',
  lightBorder: '#E0D8D0',
};

// 商品描述 HTML 样式
const descriptionStyles = `
  .product-description p { margin: 0 0 16px 0; line-height: 1.8; color: #5A5A5A; }
  .product-description h1, .product-description h2, .product-description h3,
  .product-description h4, .product-description h5, .product-description h6 {
    margin: 24px 0 12px 0; font-weight: 500; color: #2C2C2C;
  }
  .product-description ul, .product-description ol { margin: 0 0 16px 0; padding-left: 24px; }
  .product-description li { margin: 8px 0; }
  .product-description img { max-width: 100%; height: auto; border-radius: 8px; margin: 12px 0; }
  .product-description table { width: 100%; border-collapse: collapse; margin: 16px 0; }
  .product-description th, .product-description td { border: 1px solid #E0D8D0; padding: 12px; text-align: left; }
  .product-description th { background: #F8F6F1; font-weight: 500; color: #7D6B55; }
  .product-description blockquote {
    border-left: 3px solid #6B8E7C; margin: 16px 0; padding: 12px 16px;
    background: #F8F6F1; border-radius: 4px; color: #5A5A5A;
  }
  .product-description pre {
    background: #F8F6F1; padding: 16px; border-radius: 8px;
    overflow-x: auto; font-family: monospace; font-size: 13px; color: #5A5A5A;
  }
  .product-description hr { border: none; border-top: 1px solid #E0D8D0; margin: 24px 0; }
`;

export default function ProductDetailPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const screens = Grid.useBreakpoint();
  const isMobile = !screens.md;

  // 商品数据状态
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);

  // 选中的变体 ID
  const [selectedVariantValue, setSelectedVariantValue] = useState(null);

  // 数量
  const [quantity, setQuantity] = useState(1);

  // 当前展示的大图索引
  const [mainImageIndex, setMainImageIndex] = useState(0);

  // 加入购物车加载态
  const [addingToCart, setAddingToCart] = useState(false);

  // 页面加载时拉取商品详情
  useEffect(() => {
    if (slug) {
      fetchProduct(slug);
    }
  }, [slug]);

  /** 根据 slug 获取商品详情 */
  const fetchProduct = async (slug) => {
    setLoading(true);
    try {
      const data = await productApi.getBySlug(slug);
      setProduct(data);
      // 默认选中第一个变体
      if (data?.variants?.length > 0) {
        const firstValue = data.variants[0].option1Value || data.variants[0].title;
        setSelectedVariantValue(firstValue || null);
      }
    } catch {
      message.error('商品加载失败');
    } finally {
      setLoading(false);
    }
  };

  /**
   * 从变体数组中提取所有唯一的款式值
   * 优先使用 option1Value，如果没有则使用 title
   */
  const variantValues = useMemo(() => {
    if (!product?.variants) return [];
    const values = product.variants
      .map((v) => v.option1Value || v.title)
      .filter(Boolean);
    return [...new Set(values)];
  }, [product]);

  /**
   * 根据选中的款式值匹配对应变体
   */
  const selectedVariant = useMemo(() => {
    if (!product?.variants) return null;
    return (
      product.variants.find((v) =>
        v.option1Value === selectedVariantValue || (!v.option1Value && v.title === selectedVariantValue)
      ) || product.variants[0]
    );
  }, [product, selectedVariantValue]);

  /** 处理款式选择 */
  const handleVariantChange = (val) => {
    setSelectedVariantValue(val);
  };

  /** 加入购物车 */
  const handleAddToCart = async () => {
    if (!selectedVariant) {
      message.warning('请选择商品规格');
      return;
    }
    setAddingToCart(true);
    try {
      await addToCart(selectedVariant.id, quantity);
    } catch {
      message.error('加入购物车失败，请重试');
    } finally {
      setAddingToCart(false);
    }
  };

  /** 立即购买 */
  const handleBuyNow = async () => {
    if (!selectedVariant) {
      message.warning('请选择商品规格');
      return;
    }
    // 先加入购物车，然后跳转到结算页
    setAddingToCart(true);
    try {
      await addToCart(selectedVariant.id, quantity);
      navigate('/checkout');
    } catch {
      message.error('操作失败，请重试');
    } finally {
      setAddingToCart(false);
    }
  };

  // 商品图片列表（images 数组）
  const images = product?.images || [];
  const mainImage = images[mainImageIndex]?.url || null;

  // 格式化价格：直接显示美元（后端已改为元为单位）
  const formatPrice = (amount) => `$${(amount || 0).toFixed(2)}`;

  // 库存状态
  const inStock = selectedVariant
    ? (selectedVariant.inventoryQty ?? selectedVariant.inventoryQuantity ?? 1) > 0
    : true;

  if (loading) {
    return (
      <StorefrontLayout>
        <div style={{ textAlign: 'center', padding: '120px 0' }}>
          <Spin size="large" />
        </div>
      </StorefrontLayout>
    );
  }

  if (!product) {
    return (
      <StorefrontLayout>
        <div style={{ textAlign: 'center', padding: '120px 0' }}>
          <Text style={{ color: COLORS.lightInk }}>商品不存在</Text>
          <br />
          <Button
            type="link"
            onClick={() => navigate('/')}
            style={{ color: COLORS.teaBrown }}
          >
            返回首页
          </Button>
        </div>
      </StorefrontLayout>
    );
  }

  return (
    <StorefrontLayout>
      <style>{descriptionStyles}</style>
      <div style={{ maxWidth: 1200, margin: '0 auto', padding: '24px 24px 48px' }}>
        {/* 美化面包屑导航 */}
        <Breadcrumb
          separator={<span style={{ color: `${COLORS.teaBrown}30`, margin: '0 8px' }}> / </span>}
          items={[
            {
              href: '/',
              title: (
                <span
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    color: COLORS.teaBrown,
                    transition: 'color 0.2s',
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.color = COLORS.cinnabar)}
                  onMouseLeave={(e) => (e.currentTarget.style.color = COLORS.teaBrown)}
                >
                  <HomeOutlined />
                  <span style={{ marginLeft: 4 }}>首页</span>
                </span>
              ),
            },
            {
              title: (
                <span style={{ color: COLORS.lightInk }}>
                  {product.category?.name || '商品'}
                </span>
              ),
            },
            {
              title: (
                <span style={{ color: COLORS.ink, fontWeight: 500 }}>
                  {product.title}
                </span>
              ),
            },
          ]}
          style={{
            marginBottom: isMobile ? 16 : 24,
            padding: isMobile ? '12px 16px' : '16px 20px',
            background: `${COLORS.teaBrown}05`,
            borderRadius: 8,
            border: `1px solid ${COLORS.teaBrown}10`,
          }}
        />

        <Row gutter={[32, 24]}>
          {/* ===== 左侧：图片画廊 ===== */}
          <Col xs={24} md={12}>
            {/* 主图 */}
            <div
              style={{
                background: `${COLORS.teaBrown}08`,
                borderRadius: 12,
                overflow: 'hidden',
                marginBottom: 12,
                position: 'relative',
                paddingTop: '120%',
              }}
            >
              <div
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                }}
              >
                {mainImage ? (
                  <img
                    src={mainImage}
                    alt={product.title}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                    }}
                  />
                ) : (
                  <div style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    color: `${COLORS.teaBrown}30`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexDirection: 'column',
                  }}>
                    <ShoppingCartOutlined style={{ fontSize: 64, marginBottom: 8 }} />
                    <Text style={{ color: COLORS.lightInk }}>暂无图片</Text>
                  </div>
                )}
              </div>
            </div>

            {/* 缩略图列表 */}
            {images.length > 1 && (
              <Space wrap>
                {images.map((img, idx) => (
                  <div
                    key={img.id || idx}
                    onClick={() => setMainImageIndex(idx)}
                    style={{
                      width: 72,
                      height: 72,
                      borderRadius: 8,
                      overflow: 'hidden',
                      cursor: 'pointer',
                      border: mainImageIndex === idx ? `2px solid ${COLORS.teaBrown}` : `2px solid ${COLORS.lightBorder}`,
                      flexShrink: 0,
                    }}
                  >
                    <img
                      src={img.url}
                      alt={`缩略图${idx + 1}`}
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                  </div>
                ))}
              </Space>
            )}
          </Col>

          {/* ===== 右侧：商品信息 ===== */}
          <Col xs={24} md={12}>
            {/* 商品标题 */}
            <Title
              level={2}
              style={{
                marginBottom: 8,
                color: COLORS.ink,
                fontSize: 24,
                fontWeight: 500,
              }}
            >
              {product.title}
            </Title>

            {/* 价格区 */}
            <Space align="baseline" style={{ marginBottom: 16 }}>
              {/* 选中变体价格：库存为0时划线显示 */}
              <Text
                style={{
                  fontSize: 28,
                  fontWeight: 600,
                  color: COLORS.cinnabar,
                  textDecoration: inStock ? 'none' : 'line-through',
                }}
              >
                {selectedVariant ? formatPrice(selectedVariant.price) : '—'}
              </Text>
              {/* 比较价（划线原价） */}
              {selectedVariant?.compareAtPrice && (
                <Text
                  delete
                  style={{ fontSize: 16, color: COLORS.lightInk }}
                >
                  {formatPrice(selectedVariant.compareAtPrice)}
                </Text>
              )}
            </Space>

            {/* 库存状态标签 */}
            <div style={{ marginBottom: 20 }}>
              {inStock ? (
                <Tag
                  icon={<CheckCircleOutlined />}
                  style={{
                    background: `${COLORS.bambooGreen}15`,
                    borderColor: COLORS.bambooGreen,
                    color: COLORS.bambooGreen,
                  }}
                >
                  有货
                </Tag>
              ) : (
                <Tag
                  icon={<CloseCircleOutlined />}
                  style={{
                    background: `${COLORS.cinnabar}15`,
                    borderColor: COLORS.cinnabar,
                    color: COLORS.cinnabar,
                  }}
                >
                  缺货
                </Tag>
              )}
            </div>

            <Divider style={{ borderColor: `${COLORS.teaBrown}15` }} />

            {/* ===== 变体选择器 ===== */}
            {variantValues.length > 1 && (
              <div style={{ marginBottom: 20 }}>
                <Text
                  style={{
                    display: 'block',
                    marginBottom: 10,
                    color: COLORS.ink,
                    fontWeight: 500,
                  }}
                >
                  款式：
                  <Text style={{ fontWeight: 400, marginLeft: 8, color: COLORS.lightInk }}>
                    {selectedVariantValue}
                  </Text>
                </Text>
                <Space wrap>
                  {variantValues.map((val) => {
                    const variantForVal = product.variants.find(
                      (v) => v.option1Value === val || (!v.option1Value && v.title === val)
                    );
                    const outOfStock = variantForVal
                      ? (variantForVal.inventoryQty ?? variantForVal.inventoryQuantity ?? 1) <= 0
                      : false;
                    return (
                      <Button
                        key={val}
                        onClick={() => handleVariantChange(val)}
                        style={{
                          borderRadius: 6,
                          minWidth: 64,
                          borderColor: selectedVariantValue === val ? COLORS.teaBrown : COLORS.lightBorder,
                          color: selectedVariantValue === val ? '#fff' : COLORS.ink,
                          background: selectedVariantValue === val ? COLORS.teaBrown : 'transparent',
                          textDecoration: outOfStock ? 'line-through' : 'none',
                          opacity: outOfStock ? 0.5 : 1,
                        }}
                      >
                        {val}
                      </Button>
                    );
                  })}
                </Space>
              </div>
            )}

            {/* 无变体选择器时显示提示 */}
            {variantValues.length <= 1 && (
              <div style={{ marginBottom: 20 }}>
                <Tag
                  style={{
                    background: `${COLORS.bambooGreen}15`,
                    borderColor: COLORS.bambooGreen,
                    color: COLORS.bambooGreen,
                  }}
                >
                  当前款式
                </Tag>
                <Text style={{ marginLeft: 8, color: COLORS.lightInk }}>
                  {selectedVariant?.title || '默认款'}
                </Text>
              </div>
            )}

            {/* 数量选择器 */}
            <div style={{ marginBottom: 24 }}>
              <Text style={{ display: 'block', marginBottom: 10, color: COLORS.ink, fontWeight: 500 }}>
                数量：
              </Text>
              <InputNumber
                min={1}
                max={selectedVariant?.inventoryQty ?? selectedVariant?.inventoryQuantity ?? 999}
                value={quantity}
                onChange={(val) => setQuantity(val || 1)}
                size="large"
                style={{
                  width: 120,
                  borderRadius: 8,
                  borderColor: COLORS.lightBorder,
                }}
              />
            </div>

            {/* 操作按钮组：加入购物车 + 立即购买 */}
            <Space.Compact style={{ width: '100%', gap: 12, display: 'flex' }}>
              <Button
                size="large"
                icon={<ShoppingCartOutlined style={{ color: COLORS.teaBrown }} />}
                loading={addingToCart}
                disabled={!inStock}
                onClick={handleAddToCart}
                style={{
                  flex: 1,
                  height: 52,
                  fontSize: 16,
                  borderRadius: 8,
                  borderColor: COLORS.teaBrown,
                  color: COLORS.teaBrown,
                }}
              >
                {inStock ? '加入购物车' : '暂时缺货'}
              </Button>
              <Button
                size="large"
                icon={<ThunderboltOutlined />}
                loading={addingToCart}
                disabled={!inStock}
                onClick={handleBuyNow}
                style={{
                  flex: 1,
                  height: 52,
                  fontSize: 16,
                  borderRadius: 8,
                  background: COLORS.cinnabar,
                  borderColor: COLORS.cinnabar,
                  color: '#fff',
                }}
              >
                立即购买
              </Button>
            </Space.Compact>

            {/* ===== 商品描述 ===== */}
            {(product.description || product.bodyHtml) && (
              <div style={{ marginTop: 32, marginBottom: 24 }}>
                <Divider style={{ borderColor: `${COLORS.teaBrown}15` }} />
                <div
                  className="product-description"
                  style={{
                    background: `${COLORS.teaBrown}08`,
                    borderRadius: 8,
                    padding: '20px 24px',
                    fontSize: 14,
                    wordBreak: 'break-word',
                    whiteSpace: 'pre-wrap',
                  }}
                  dangerouslySetInnerHTML={{
                    __html: product.bodyHtml
                      ? product.bodyHtml
                      : product.description
                        ? product.description
                            .split('\n')
                            .filter(line => line.trim())
                            .map(line => `<p style="margin: 0 0 12px 0; line-height: 1.8; word-break: break-word;">${line}</p>`)
                            .join('')
                        : ''
                  }}
                />
              </div>
            )}
          </Col>
        </Row>
      </div>
    </StorefrontLayout>
  );
}
